import { useCallback, useEffect, useState } from 'react';

import { api } from '../api.ts';
import { useJog, useModosithat, useMuvelet, useTorolhet } from '../felhasznalo-context.ts';
import { Levelnaplo } from '../reszek/Levelnaplo.tsx';
import { Mezo } from '../reszek/Mezo.tsx';
import { KiseroLevelek, type LevelSablon } from '../reszek/KiseroLevelek.tsx';
import { Sablonok } from './Sablonok.tsx';

/**
 * E-mail küldés beállításai. (3.0 / 4.)
 *
 * Több postafiók vehető fel, és levéltípusonként megmondható, melyikről
 * menjen a levél. A **jelszó csak a szerveren tárolódik**, a böngészőbe
 * nem kerül le — a listában csak az látszik, be van-e állítva.
 *
 * A **teszt mód** minden kimenő levelet egyetlen belső címre terel, a
 * tárgyban jelölve, kinek ment volna. A gyakorló példányon ez lesz
 * bekapcsolva, az élesen kikapcsolva.
 */

interface Postafiok {
  id: number;
  nev: string;
  email: string;
  feladonev: string | null;
  host: string;
  port: number;
  titkositas: string;
  felhasznalonev: string | null;
  van_jelszo: boolean;
  aktiv: boolean;
}

interface EmailValasz {
  postafiokok: Postafiok[];
  hozzarendeles: Array<{ tipus: string; nev: string; postafiok_id: number | null }>;
  archiv: { be: boolean; cim: string };
  teszt: { be: boolean; cim: string };
  titkositasok: string[];
  sikertelen: number;
  levelek: LevelSablon[];
}

const URES_FIOK = { nev: '', email: '', host: '', port: 465, titkositas: 'ssl' };

export function EmailBeallitas() {
  /** Három dolog egy helyen: a postafiókok, az okmányok és a levelek szövegei. */
  const [ful, setFul] = useState<'fiokok' | 'dokumentumok' | 'levelek' | 'naplo'>('fiokok');
  const [adat, setAdat] = useState<EmailValasz | null>(null);
  const [hiba, setHiba] = useState<string | null>(null);
  const [ujFiok, setUjFiok] = useState<typeof URES_FIOK | null>(null);
  const modosithat = useModosithat('email');
  const torolhet = useTorolhet();
  const sablonJog = useJog('sablon');
  const kuldhet = useMuvelet('email_kuldes');
  const [proba, setProba] = useState(false);
  const [uzenet, setUzenet] = useState<string | null>(null);

  /** Próbalevél: a fiók tényleges kipróbálása, nem csak a mezők ellenőrzése. */
  async function probalevel() {
    setProba(true);
    setHiba(null);
    setUzenet(null);
    try {
      const v = await api.post<{ cimzett: string; fiok: string }>('/api/level/proba', {});
      setUzenet(`Próbalevél elküldve a(z) „${v.fiok}" fiókról ide: ${v.cimzett}.`);
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A próbalevél nem ment ki.');
    } finally {
      setProba(false);
    }
  }

  const betolt = useCallback(async () => {
    try {
      const v = await api.get<EmailValasz>('/api/email');
      setAdat(v);
      setHiba(null);
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A beállításokat nem sikerült betölteni.');
    }
  }, []);

  useEffect(() => {
    void betolt();
  }, [betolt]);

  async function hivas(mit: () => Promise<EmailValasz>) {
    try {
      const v = await mit();
      setAdat(v);
      setHiba(null);
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A művelet nem sikerült.');
    }
  }

  const mezo = (p: Postafiok, k: string, ertek: string | number | null, osztaly = 'txt') => (
    <Mezo
      ertek={ertek}
      osztaly={osztaly}
      tiltva={!modosithat}
      onMent={async (uj) => {
        await hivas(() => api.patch<EmailValasz>(`/api/email/postafiok/${p.id}/${k}`, { ertek: uj }));
      }}
    />
  );

  return (
    <>
      <div className="dayhead">
        <span style={{ fontSize: 15, fontWeight: 600 }}>E-mail és sablonok</span>
        <div className="vsw">
          <button className={ful === 'fiokok' ? 'on' : undefined} onClick={() => setFul('fiokok')}>
            Postafiókok
          </button>
          {sablonJog > 0 && (
            <button
              className={ful === 'dokumentumok' ? 'on' : undefined}
              onClick={() => setFul('dokumentumok')}
            >
              Dokumentum sablonok
            </button>
          )}
          <button className={ful === 'levelek' ? 'on' : undefined} onClick={() => setFul('levelek')}>
            Kísérő levelek
          </button>
          {kuldhet && (
            <button className={ful === 'naplo' ? 'on' : undefined} onClick={() => setFul('naplo')}>
              Levélnapló
            </button>
          )}
        </div>
        <span className="hint">
          {ful === 'fiokok'
            ? 'Ezekről a fiókokról mennek ki a levelek. A jelszót a rendszer a szerveren tárolja.'
            : ful === 'dokumentumok'
              ? 'Ami magára az okmányra kerül. A kiállított okmányokon a kiállításkori szöveg marad.'
              : ful === 'naplo'
                ? 'Minden kiküldött levél, a sikertelenek is — a hiba szövegével együtt.'
                : 'Ami a levélben áll, amivel a dokumentum kimegy.'}
        </span>
      </div>

      {hiba && <div className="uzenet bad">{hiba}</div>}
      {uzenet && (
        <div className="uzenet ok" onClick={() => setUzenet(null)}>
          {uzenet}
        </div>
      )}

      {ful === 'dokumentumok' && sablonJog > 0 && <Sablonok sajatFejlec={false} />}

      {ful === 'naplo' && kuldhet && <Levelnaplo onHiba={setHiba} />}

      {ful === 'levelek' && (
        <KiseroLevelek
          levelek={adat?.levelek ?? []}
          onValtozott={(uj) =>
            setAdat((e) =>
              e ? { ...e, levelek: e.levelek.map((l) => (l.tipus === uj.tipus ? uj : l)) } : e,
            )
          }
          onHiba={setHiba}
        />
      )}

      {ful === 'fiokok' && (
      <>


      <div className="panel" style={{ marginTop: 16 }}>
        <p className="lbl">Postafiókok</p>
        <div className="matrix">
          <table className="tt2">
            <thead>
              <tr>
                <th>Megnevezés</th>
                <th>E-mail cím</th>
                <th>Feladó neve</th>
                <th>Kimenő szerver</th>
                <th className="n">Port</th>
                <th>Titkosítás</th>
                <th>Felhasználónév</th>
                <th>Jelszó</th>
                <th className="n">Aktív</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(adat?.postafiokok ?? []).map((p) => (
                <tr key={p.id} className={p.aktiv ? undefined : 'nul'}>
                  <td>{mezo(p, 'nev', p.nev)}</td>
                  <td>{mezo(p, 'email', p.email)}</td>
                  <td>{mezo(p, 'feladonev', p.feladonev)}</td>
                  <td>{mezo(p, 'host', p.host)}</td>
                  <td>{mezo(p, 'port', p.port, 'c')}</td>
                  <td>
                    <select
                      value={p.titkositas}
                      disabled={!modosithat}
                      onChange={(e) =>
                        void hivas(() =>
                          api.patch<EmailValasz>(`/api/email/postafiok/${p.id}/titkositas`, {
                            ertek: e.target.value,
                          }),
                        )
                      }
                    >
                      {(adat?.titkositasok ?? []).map((t) => (
                        <option key={t} value={t}>
                          {t === 'ssl' ? 'SSL' : t === 'starttls' ? 'STARTTLS' : 'nincs'}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>{mezo(p, 'felhasznalonev', p.felhasznalonev)}</td>
                  <td>
                    <Mezo
                      ertek=""
                      osztaly="txt"
                      helykitolto={p.van_jelszo ? 'beállítva — átíráshoz írd be' : 'nincs beállítva'}
                      tiltva={!modosithat}
                      onMent={async (uj) => {
                        if (!uj) return;
                        await hivas(() =>
                          api.patch<EmailValasz>(`/api/email/postafiok/${p.id}/jelszo`, { ertek: uj }),
                        );
                      }}
                    />
                  </td>
                  <td className="c">
                    <input
                      type="checkbox"
                      checked={p.aktiv}
                      disabled={!modosithat}
                      onChange={(e) =>
                        void hivas(() =>
                          api.patch<EmailValasz>(`/api/email/postafiok/${p.id}/aktiv`, {
                            ertek: e.target.checked,
                          }),
                        )
                      }
                    />
                  </td>
                  <td className="act">
                    {torolhet && (
                      <button
                        className="btn sm torlo"
                        onClick={() =>
                          void hivas(() => api.torol<EmailValasz>(`/api/email/postafiok/${p.id}`))
                        }
                      >
                        Töröl
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {(adat?.postafiokok.length ?? 0) === 0 && (
                <tr>
                  <td colSpan={10} style={{ padding: 20, textAlign: 'center', color: '#A3A9B0' }}>
                    Még nincs postafiók.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {modosithat &&
          (ujFiok ? (
            <div className="npform" style={{ marginTop: 10 }}>
              <h4>Új postafiók</h4>
              <div className="urlap">
                <label>
                  Megnevezés
                  <input
                    value={ujFiok.nev}
                    onChange={(e) => setUjFiok({ ...ujFiok, nev: e.target.value })}
                  />
                </label>
                <label>
                  E-mail cím
                  <input
                    value={ujFiok.email}
                    onChange={(e) => setUjFiok({ ...ujFiok, email: e.target.value })}
                  />
                </label>
                <label>
                  Kimenő szerver
                  <input
                    value={ujFiok.host}
                    onChange={(e) => setUjFiok({ ...ujFiok, host: e.target.value })}
                  />
                </label>
                <label>
                  Port
                  <input
                    value={String(ujFiok.port)}
                    onChange={(e) => setUjFiok({ ...ujFiok, port: Number(e.target.value) || 465 })}
                  />
                </label>
              </div>
              <div className="btns">
                <button
                  className="btn sm pri"
                  onClick={async () => {
                    await hivas(() => api.post<EmailValasz>('/api/email/postafiok', ujFiok));
                    setUjFiok(null);
                  }}
                >
                  Felvétel
                </button>
                <button className="btn sm" onClick={() => setUjFiok(null)}>
                  Mégse
                </button>
              </div>
            </div>
          ) : (
            <button className="btn sm" style={{ marginTop: 10 }} onClick={() => setUjFiok({ ...URES_FIOK })}>
              + Új postafiók
            </button>
          ))}

        {/* Egy kattintás: tényleg be tud-e lépni a kimenő szerverre. */}
        {kuldhet && (adat?.postafiokok.length ?? 0) > 0 && (
          <div className="btns" style={{ marginTop: 12 }}>
            <button className="btn sm" disabled={proba} onClick={() => void probalevel()}>
              {proba ? 'küldés…' : 'Próbalevél küldése'}
            </button>
            <span className="hint">
              Belép a kimenő szerverre, és küld egy levelet az archív címre.
            </span>
          </div>
        )}
      </div>

      <div className="panel" style={{ marginTop: 16, maxWidth: 760 }}>
        <p className="lbl">Melyik levél melyik fiókról menjen</p>
        {(adat?.hozzarendeles ?? []).map((h) => (
          <div className="cegsor" key={h.tipus}>
            <label>{h.nev}</label>
            <select
              value={h.postafiok_id === null ? '' : String(h.postafiok_id)}
              disabled={!modosithat}
              onChange={(e) =>
                void hivas(() =>
                  api.patch<EmailValasz>(`/api/email/hozzarendeles/${h.tipus}`, {
                    postafiok_id: e.target.value === '' ? null : Number(e.target.value),
                  }),
                )
              }
            >
              <option value="">— nincs megadva —</option>
              {(adat?.postafiokok ?? []).map((p) => (
                <option key={p.id} value={String(p.id)}>
                  {p.nev}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>

      <div className="panel" style={{ marginTop: 16, maxWidth: 760 }}>
        <p className="lbl">Archív másolat</p>
        <div className="hint" style={{ margin: '-4px 0 10px' }}>
          Rejtett címzettként megy, a partner nem látja.
        </div>
        <div className="cegsor">
          <label>Cím</label>
          <Mezo
            ertek={adat?.archiv.cim ?? ''}
            tiltva={!modosithat}
            helykitolto="még nincs megadva"
            onMent={async (uj) => {
              await hivas(() =>
                api.patch<EmailValasz>('/api/email/kapcsolo/archiv', {
                  be: adat?.archiv.be ?? false,
                  cim: uj,
                }),
              );
            }}
          />
        </div>
        <label className="kapcs">
          <input
            type="checkbox"
            checked={adat?.archiv.be ?? false}
            disabled={!modosithat}
            onChange={(e) =>
              void hivas(() =>
                api.patch<EmailValasz>('/api/email/kapcsolo/archiv', {
                  be: e.target.checked,
                  cim: adat?.archiv.cim ?? '',
                }),
              )
            }
          />
          Archív másolat küldése
        </label>
      </div>

      <div className="panel" style={{ marginTop: 16, maxWidth: 760 }}>
        <p className="lbl">Teszt mód</p>
        <div className="hint" style={{ margin: '-4px 0 10px' }}>
          Bekapcsolva minden kimenő levél erre a címre megy, a tárgyban jelölve, kinek ment volna.
          A dokumentum ugyanúgy elkészül és tárolódik. A gyakorló példányon legyen bekapcsolva, az
          élesen kikapcsolva.
        </div>
        <div className="cegsor">
          <label>Belső cím</label>
          <Mezo
            ertek={adat?.teszt.cim ?? ''}
            tiltva={!modosithat}
            helykitolto="még nincs megadva"
            onMent={async (ertek) => {
              await hivas(() =>
                api.patch<EmailValasz>('/api/email/kapcsolo/teszt', {
                  be: adat?.teszt.be ?? false,
                  cim: ertek,
                }),
              );
            }}
          />
        </div>
        <label className="kapcs">
          <input
            type="checkbox"
            checked={adat?.teszt.be ?? false}
            disabled={!modosithat}
            onChange={(e) =>
              void hivas(() =>
                api.patch<EmailValasz>('/api/email/kapcsolo/teszt', {
                  be: e.target.checked,
                  cim: adat?.teszt.cim ?? '',
                }),
              )
            }
          />
          Teszt mód bekapcsolva
        </label>
      </div>

      {(adat?.sikertelen ?? 0) > 0 && (
        <div className="uzenet flag" style={{ marginTop: 16 }}>
          {adat?.sikertelen} levél nem ment ki. Az újraküldés a levélnaplóból indítható.
        </div>
      )}
      </>
      )}
    </>
  );
}
